import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useStewardStore } from '@/stores/useStewardStore'
import { getStewardNote, type StewardNote } from '@/lib/api'

interface Props {
  kind: string
  /** Folder path (job) or JD code (structure). */
  id: string
  children: React.ReactNode
}

// Module-level peek cache — a reference hovered once never refetches.
const peekCache = new Map<string, StewardNote>()
const HOVER_DELAY = 220

/**
 * An inline `steward://` reference rendered as a live handle. Hover (on pointer devices)
 * shows a lightweight peek of the entity; click opens it as a full card in the chat. The
 * link text is the model's own words; the kind+id resolve via /steward/note. Unresolvable
 * references just show their error in the peek and still render as plain underlined text.
 */
export function ReferenceLink({ kind, id, children }: Props) {
  const appendNoteCard = useStewardStore((s) => s.appendNoteCard)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cacheKey = `${kind}:${id}`
  const [peek, setPeek] = useState<{ x: number; y: number } | null>(null)
  const [note, setNote] = useState<StewardNote | null>(peekCache.get(cacheKey) ?? null)
  const [error, setError] = useState<string | null>(null)

  const label = typeof children === 'string' ? children : undefined

  const load = useCallback(() => {
    if (peekCache.has(cacheKey)) {
      setNote(peekCache.get(cacheKey)!)
      return
    }
    getStewardNote(kind, id)
      .then((n) => {
        peekCache.set(cacheKey, n)
        setNote(n)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Not found'))
  }, [cacheKey, kind, id])

  // Hover only matters on devices that actually hover (touch falls back to click → card).
  const onEnter = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const r = anchorRef.current?.getBoundingClientRect()
      if (!r) return
      // Clamp x so a 320px popover stays on-screen; sit just below the link.
      const x = Math.min(r.left, window.innerWidth - 332)
      setPeek({ x: Math.max(8, x), y: r.bottom + 6 })
      load()
    }, HOVER_DELAY)
  }, [load])

  const onLeave = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    setPeek(null)
  }, [])

  const onClick = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    setPeek(null)
    appendNoteCard({ noteKind: kind, id, title: label })
  }, [appendNoteCard, kind, id, label])

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={onClick}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        className="inline cursor-pointer rounded-[3px] bg-foreground/[0.06] px-1 text-foreground underline decoration-border decoration-dotted underline-offset-2 transition-colors hover:bg-foreground/[0.12] hover:decoration-foreground"
      >
        {children}
      </button>

      {peek &&
        createPortal(
          <div
            style={{ position: 'fixed', left: peek.x, top: peek.y, width: 320, zIndex: 60 }}
            className="pointer-events-none rounded-lg border border-border bg-background p-3 text-[13px] leading-relaxed shadow-lg"
          >
            {error ? (
              <div className="text-[12px] text-muted-foreground">Couldn't preview this reference.</div>
            ) : !note ? (
              <div className="text-[12px] font-light text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="mb-1 truncate text-[11px] font-light uppercase tracking-[0.06em] text-muted-foreground">
                  {note.kind} · {note.title}
                </div>
                <div className="line-clamp-5 whitespace-pre-wrap text-foreground/90">
                  {peekText(note.body) || <span className="text-muted-foreground">(no notes yet)</span>}
                </div>
                {note.files.length > 0 && (
                  <div className="mt-1.5 text-[11px] font-light text-muted-foreground">
                    {note.files.length} file{note.files.length === 1 ? '' : 's'} · click to open
                  </div>
                )}
              </>
            )}
          </div>,
          document.body
        )}
    </>
  )
}

/** A plain-text peek: strip the heaviest markdown so the popover reads cleanly, trim long. */
function peekText(body: string): string {
  const t = body
    .replace(/<!--[\s\S]*?-->/g, '')        // HTML comments (the governing-note scaffolding)
    .replace(/^#{1,6}\s+/gm, '')             // heading hashes
    .replace(/[*_`>]/g, '')                  // emphasis / code / quote markers
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return t.length > 280 ? t.slice(0, 280).trimEnd() + '…' : t
}
